import {
  AdminInput,
  AdminSpinner,
  Button,
  Disclaimer,
  Div,
  H3,
  Img,
  ListSpinner,
  ModalPortal,
  P,
  TrashIconRed,
  lightTheme,
  useAuth,
} from '@vgrubs/components';
import { useState } from 'react';
import { NoDataComponent } from '../../../adminComponents/NoDataComponent/NoDataComponent';
import { UserRole } from '../../../types/user.types';
import { useMenuAiImageLibrary } from './useMenuAiImageLibrary';

/**
 * "Generated images" picker — a searchable, infinite grid of previously
 * generated images (global across all brands). Opened on top of
 * GenerateImageModal; picking a cell hands its `url` back via `onSelect` (the
 * parent then reuses its existing preview + "Use this image" accept flow, so no
 * image is generated). Search is prefilled with the item name via `initialSearch`.
 */
export const ImageLibraryModal = ({
  isOpen,
  closeModal,
  initialSearch,
  onSelect,
}: {
  isOpen: boolean;
  closeModal: () => void;
  initialSearch?: string;
  onSelect: (url: string) => void;
}) => {
  const {
    images,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    isFetching,
    hasNextPage,
    query,
    deleteImage,
    deletingId,
  } = useMenuAiImageLibrary({ enabled: isOpen, initialSearch });

  // Deleting from the shared library is destructive and global, so it's admins
  // only (the backend enforces it too).
  const isAdmin = useAuth((state) => state?.user?.role) === UserRole.admin;
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // A search/refetch (not pagination) — overlay the spinner over the results.
  const showOverlay = isFetching && !isFetchingNextPage;
  const showEmpty = images.length === 0 && !isFetching;

  return (
    <>
      <ModalPortal
        isOpen={isOpen}
        closeModal={closeModal}
        header="Generated images"
        subtitle="Reuse an image instead of generating a new one"
        logo={false}
        zIndex={130}
        textAlign="left"
        minHeight="80vh"
        maxHeight="80vh"
        width="800px"
      >
        <Div $flex $column $gap="20px">
          <AdminInput
            search
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Search by item name or description"
          />
          {showEmpty ? (
            <NoDataComponent
              description={
                query
                  ? `No images found for "${query}".`
                  : 'No generated images yet.'
              }
              height="calc(80vh - (85px + 40px))"
              heightSM="calc(80vh - (85px + 40px))"
            />
          ) : (
            // auto-FILL keeps the empty tracks, so a lone image stays at one
            // column's width instead of stretching across the whole row (auto-fit
            // would collapse the empty track and blow it up to full width). Two
            // images still fill the row.
            <Div
              $grid
              $gridTemplateColumns="repeat(auto-fill, minmax(180px, 1fr))"
              $gap="16px"
              $width="100%"
            >
              {images.map((img) => (
                <Div
                  $pointer
                  key={img.id}
                  onClick={() => onSelect(img.url)}
                  style={
                    deletingId === img.id
                      ? { pointerEvents: 'none' }
                      : undefined
                  }
                >
                  <Div $relative>
                    {deletingId === img.id && <AdminSpinner absolute small />}
                    <Img
                      src={img.url}
                      $width="100%"
                      $borderRadius="12px"
                      // No Img props for these — keep inline.
                      style={{
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    {isAdmin && (
                      <Div
                        $absolute
                        // Sits on the image's top-right corner; stopPropagation so
                        // the tile's "pick this image" click doesn't also fire.
                        style={{ top: '8px', right: '8px', lineHeight: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteId(img.id);
                        }}
                      >
                        <TrashIconRed />
                      </Div>
                    )}
                  </Div>
                  {img.itemName && (
                    <Disclaimer
                      $m="0"
                      $mt="6"
                      $left
                      $color={lightTheme.darkGray}
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {img.itemName}
                    </Disclaimer>
                  )}
                </Div>
              ))}
            </Div>
          )}

          {showOverlay && (
            <AdminSpinner absolute small overflowBorderRadius="30px" />
          )}

          {hasNextPage && (
            <ListSpinner
              loading={isFetchingNextPage}
              observerRef={observerRef}
              smallest
              height="36px"
              heightSM="36px"
              mb="0"
            />
          )}
        </Div>
      </ModalPortal>

      {/* Sibling (not a child) of the library modal, and a portal of its own —
          nesting it inside would put it under the library's framer-motion
          transform, which breaks fixed positioning. */}
      <ModalPortal
        isOpen={!!confirmDeleteId}
        closeModal={() => setConfirmDeleteId(null)}
        logo={false}
        zIndex={140}
      >
        <H3 $mb="8" $center $color={lightTheme.primary}>
          Are you sure
        </H3>
        <P $mb="24">
          Are you sure you want to delete this image? This cannot be undone.
        </P>

        <Div $flex $gap="16px">
          <Button
            $selected
            $w="100%"
            onClick={() => {
              if (confirmDeleteId) deleteImage(confirmDeleteId);
              setConfirmDeleteId(null);
            }}
          >
            Delete
          </Button>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setConfirmDeleteId(null)}
          >
            Cancel
          </Button>
        </Div>
      </ModalPortal>
    </>
  );
};
